// App.js — Composição principal da SPA.
// Ordem da home conforme PDF "Landing PAge" (Figma):
//   Hero → Produtos em Destaque → Especial da Casa → Avaliações dos leitores
//   → Você poderia gostar → Autores Publicados → Portfolio → FAQ → Contato
import React from "react";
import { BrowserRouter as Router, Route, Routes } from "react-router-dom";

// Layout / globais
import Header from "./components/shared/Header";
import Footer from "./components/shared/Footer";
import ModalCarrinho from "./components/utils/ModalCarrinho";
import PageLoader from "./components/shared/PageLoader";
import { Toaster } from "./components/utils/toast";
import VoltarAoTopo from "./components/shared/VoltarAoTopo";
import MotionProvider from "./components/ui/MotionProvider";

// Seções da página inicial
import Hero from "./components/hero/Hero";
import ProdutosDestaque from "./components/produto/cardDestaque/ProdutosDestaque";
import EspecialDaCasa from "./components/produto/especialDaCasa/EspecialDaCasa";
import CarrosselAvaliacoes from "./components/avaliacoes/CarrosselAvaliacoes";
import SliderProdutos from "./components/produto/sliderProduto/SliderProduto";
import AutoresDestaque from "./components/autoresDestaque/AutoresDestaque";
import NumerosInsigne from "./components/numeros/NumerosInsigne";
import VitrineCapas from "./components/vitrineCapas/VitrineCapas";
import Portfolio from "./components/portfolio/Portfolio";
import FAQ from "./components/faq/Faq";
import FaqPage from "./components/faq/FaqPage";
// Contato: removido da Home a pedido do cliente; ainda usado na rota /contato
import Contato from "./components/contato/Contato";

// Demais páginas
// Entrar e Criar Conta são páginas SEPARADAS (o formulário combinado agora só
// existe dentro do checkout, onde sair da página custaria o carrinho).
import EntrarPage from "./components/login/EntrarPage";
import CriarContaPage from "./components/login/CriarContaPage";
import Perfil from "./components/perfil/Perfil";
import Checkout from "./components/checkout/Checkout";
import Produto from "./components/produto/paginaProduto/PaginaProduto";
import ProdutosGrid from "./components/produto/gridProduto/GridProduto";
import AutorBiografia from "./components/autor/AutorBiografia";
import AutoresGrid from "./components/autor/AutoresGrid";
import CarrinhoPage from "./components/carrinho/CarrinhoPage";
import CompraConcluida from "./components/checkout/finalizar/CompraConcluida";
import Pagina404 from "./components/erro/Pagina404";
// Clube Insigne: a assinatura e o leitor que ela destrava.
//
// O LEITOR É CARREGADO SOB DEMANDA, e isso não é preferência: o pdf.js pesa
// ~120 kB comprimido, e num import normal ele entrava no bundle principal —
// todo visitante da home pagava o download de uma biblioteca de PDF que só
// quem abre um livro usa. Com o lazy, ele vira um pedaço separado, buscado no
// momento em que a rota /ler é aberta.
import ClubeInsigne from "./components/clube/ClubeInsigne";

// Providers de estado global
import { AuthProvider } from "./services/context/AuthContext";
import { CarrinhoProvider } from "./services/context/CarrinhoContext";
import { ModalProvider } from "./services/context/ModalContext";

// Estilização global (Tailwind + overrides)
import "./App.css";
import "./assets/styles/Reset.css";
import Fita from "./components/ui/Fita";
import Costura from "./components/ui/Costura";

const Leitor = React.lazy(() => import("./components/leitor/Leitor"));

function App() {
  const main = process.env.REACT_APP_MAIN;

  const Home = () => (
    <>
      <Hero />
      {/* A faixa laranja cruzada do site principal, logo abaixo do carrossel:
          diz em cinco palavras o que a loja é, e separa a vitrine da abertura. */}
      <Fita />
      <ProdutosDestaque nome="Produtos em Destaque" icone="bi-patch-exclamation-fill" />
      {/* Vitrine de UM livro, curado no painel. Substituiu os blocos
          "Continue de onde parou" e "Em Promoção", que mostravam livros
          aleatórios sob rótulos que não correspondiam a nada — ver o
          cabeçalho de EspecialDaCasa.js. */}
      {/* As faixas de fundo próprio (.faixa-alternada, App.css) alternam
          com as seções no fundo normal, como no site principal. */}
      <div className="faixa-alternada">
        <EspecialDaCasa />
      </div>
      {/* Prova social logo depois da vitrine: quem acabou de ver um livro
          curado encontra em seguida gente que leu e comentou. A faixa some
          sozinha enquanto não houver avaliações suficientes — ver
          MINIMO_DE_AVALIACOES em CarrosselAvaliacoes.js. */}
      <CarrosselAvaliacoes />
      <div className="faixa-alternada">
      <SliderProdutos
        nome="Você poderia gostar"
        icone="bi-stars"
        chapeu="Uma volta pelo acervo"
        // A frase descreve o que a seção REALMENTE faz: ela embaralha o
        // catálogo a cada carregamento (`random`). Chamar isso de
        // "recomendado para você" seria prometer uma personalização que não
        // existe — e a primeira pessoa a recarregar a página descobriria.
        descricao="A cada visita, uma seleção diferente do catálogo. Passe o olho: é assim que se acha o livro que não se estava procurando."
        random
        expansivo
      />
      </div>
      {/* Desfile contínuo de capas — mostra o volume do catálogo sem ocupar
          uma tela inteira de grade. */}
      <VitrineCapas />
      <Costura />
      <AutoresDestaque />
      <Portfolio />
      {/* "Nosso acervo" fecha o bloco institucional: vem logo depois do convite
          para publicar, onde os números da editora respondem a pergunta que o
          convite levanta ("quantos livros vocês já publicaram?"). */}
      <NumerosInsigne />
      <Costura invertida />
      <FAQ />
    </>
  );

  return (
    <AuthProvider>
      <CarrinhoProvider>
        <ModalProvider>
          <Router>
            {/* MotionProvider carrega só o subconjunto de animação que o site
                usa; ver o comentário em components/ui/MotionProvider.js. */}
            <MotionProvider>
            <div className="App font-sans">
              {/* Loader exibido a cada troca de rota (igual ao landing) */}
              <PageLoader />
              <Header />

              <Routes>
                <Route path={main} element={<Home />} />
                <Route path={main + "checkout/"} element={<Checkout />} />
                <Route path={main + "login/"} element={<EntrarPage />} />
                <Route path={main + "criar-conta"} element={<CriarContaPage />} />
                {/* /cadastro e /registro apontam para a mesma tela — links antigos
                    e o hábito do usuário não podem cair em 404. */}
                <Route path={main + "cadastro"} element={<CriarContaPage />} />
                <Route path={main + "registro"} element={<CriarContaPage />} />
                <Route path={main + "perfil/"} element={<Perfil />} />
                <Route path={main + "livros"} element={<ProdutosGrid />} />
                <Route path={main + "livros/:id"} element={<Produto />} />
                <Route path={main + "autores"} element={<AutoresGrid />} />
                <Route path={main + "autores/:id"} element={<AutorBiografia />} />
                <Route path={main + "faq"} element={<FaqPage />} />
                <Route path={main + "carrinho"} element={<CarrinhoPage />} />
                <Route path={main + "compra-concluida"} element={<CompraConcluida />} />
                <Route path={main + "contato"} element={<Contato />} />
                {/* Clube Insigne. `/ler/:id` é o leitor; ele confere o acesso
                    no servidor a cada página, então não precisa de guarda de
                    rota aqui — e uma guarda no cliente daria a impressão
                    errada de que a proteção mora no navegador. */}
                <Route path={main + "clube"} element={<ClubeInsigne />} />
                {/* A vitrine do livro para quem assina: a MESMA página do livro,
                    sem a parte de venda (ver o cabeçalho de PaginaProduto.js). */}
                <Route path={main + "clube/livros/:id"} element={<Produto modo="clube" />} />
                <Route
                  path={main + "ler/:id"}
                  element={
                    // O fallback repete o visual de "abrindo o livro" do
                    // próprio leitor: trocar de tela duas vezes (primeiro um
                    // spinner genérico, depois o do leitor) faria a abertura
                    // parecer mais lenta do que é.
                    <React.Suspense
                      fallback={
                        <div className="bg-insigne-bg min-h-screen pt-[var(--nav-h)] flex items-center justify-center text-white/60">
                          <i className="bi bi-arrow-repeat animate-spin mr-2" /> Abrindo o livro...
                        </div>
                      }
                    >
                      <Leitor />
                    </React.Suspense>
                  }
                />
                {/* Curinga, SEMPRE por último: o React Router casa a rota mais
                    específica, mas manter o `*` no fim deixa a intenção óbvia
                    para quem for acrescentar rota depois. Sem ele, endereço
                    fora da lista renderizava cabeçalho e rodapé com o miolo
                    vazio — nem erro, nem redirecionamento. */}
                <Route path="*" element={<Pagina404 />} />
              </Routes>

              <ModalCarrinho />
              <Toaster />
              <VoltarAoTopo />
              {/* As faíscas de clique (ClickSpark) foram REMOVIDAS em
                  20/08/2026 a pedido do cliente: o canvas fixo cobria a
                  viewport inteira e causava comportamento estranho em algumas
                  telas. Nada mais no site depende dele. */}
              <Footer />
            </div>
            </MotionProvider>
          </Router>
        </ModalProvider>
      </CarrinhoProvider>
    </AuthProvider>
  );
}

export default App;
